import { Subject, of } from 'rxjs';
import { omit, isEqual, includes, map, pickBy, identity } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { ChangeDetectorRef, Component, Inject, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { AbstractControlOptions, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { ValidationHelper } from '@diktup/frontend/helpers';

import { SeoService, SharedService } from '../../services';
import { AuthService } from '../../services/auth/auth.service';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';

@Component({
  template: '',
})
export class BaseBaguetteLoginComponent implements OnInit {
  private unsubscribeAll: Subject<any> = new Subject<any>();

  seo: any;
  activeId = 1;
  showAlert = false;
  recoverPwd = false;
  posId: string = '';
  signInForm: FormGroup;
  signUpForm: FormGroup;
  authenticated: boolean;
  isButtonDisabled = false;
  forgotPasswordForm: FormGroup;
  isResetButtonDisabled: boolean;

  constructor(
    protected router: Router,
    protected renderer: Renderer2,
    protected metaTagService: Meta,
    protected activeModal: NgbModal,
    protected seoService: SeoService,
    protected titleTagService: Title,
    protected authService: AuthService,
    protected formBuilder: FormBuilder,
    protected toastrService: ToastrService,
    protected sharedService: SharedService,
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) protected document: Document,
    @Inject(PLATFORM_ID) protected platformId: Object,
  ) {
    this.sharedService.posId$.pipe(takeUntil(this.unsubscribeAll)).subscribe((posId) => {
      this.posId = posId;
    });
    this.authService.authenticated$.pipe(takeUntil(this.unsubscribeAll)).subscribe((authenticated: boolean) => {
      this.authenticated = authenticated;
      this.changeDetectorRef.markForCheck();
    });

    this.seoService.getGeneralSEOJSON().subscribe((res) => {
      this.seo = res;
      if (this.seo?.title) {
        this.titleTagService.setTitle(this.seo?.title);
      }
      this.sharedService.tags$ = [...this.metaTagService.addTags(map(this.seo?.metas, (meta) => pickBy(meta, identity))), { charset: 'UTF-8' }];
        this.sharedService.links$ = map(this.seo?.links, (seoLink) => {
          const link = this.renderer.createElement('link');
          if (seoLink.sizes) {
            link.sizes = seoLink.sizes;
          }
          if (seoLink.href) {
            link.href = seoLink.href;
          }
          if (seoLink.rel) {
            link.rel = seoLink.rel;
          }
          this.renderer.appendChild(this.document.head, link);
          return link;
        });
    });
  }

  ngOnInit(): void {
    this.forgotPasswordForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });
    const initialValues = this.forgotPasswordForm.value;
    this.forgotPasswordForm.valueChanges.subscribe((ivalues) => {
      this.isResetButtonDisabled = isEqual(ivalues, initialValues);
    });
    this.signInForm = this.formBuilder.group({
      login: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      rememberMe: [''],
    });
    this.signUpForm = this.formBuilder.group({
      firstName: ['', [Validators.required]],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      pwd: this.formBuilder.group(
        {
          password: ['', Validators.required],
          confirmPassword: ['', Validators.required],
        },
        { validators: [ValidationHelper.matchingFields('password', 'confirmPassword')] } as AbstractControlOptions,
      ),
    });
  }

  signIn(): void {
    this.showAlert = false;
    this.isButtonDisabled = true;
    this.authService
      .loginForTarget(this.signInForm.value)
      .pipe(
        catchError(() => {
          this.isButtonDisabled = false;
          this.toastrService.error('Something went wrong!');
          this.changeDetectorRef.markForCheck();
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res?.data || res?.[0]?.data) {
          this.router.navigate(['/']);
          this.isButtonDisabled = false;
          this.changeDetectorRef.markForCheck();
        }
        if (res?.errors) {
          const error: string = res?.errors[0]?.extensions?.exception?.response?.error;
          if (includes(error, 'password')) {
            this.toastrService.error('Password is incorrect!');
          } else if (includes(error, 'email') || includes(error, 'login')) {
            this.toastrService.error('No user with this login!');
          } else {
            this.toastrService.error('Something went wrong!');
          }
          this.isButtonDisabled = false;
          this.signInForm.enable();
          this.signInForm.reset();
          this.showAlert = true;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  signUp() {
    this.isButtonDisabled = true;
    if (this.signUpForm.invalid) {
      return;
    }
    const formValue: any = {
      ...omit(this.signUpForm.value, 'pwd'),
      password: this.signUpForm.value.pwd.password,
    };
    this.authService
      .registerForTarget(formValue)
      .pipe(
        catchError(() => {
          this.isButtonDisabled = false;
          this.toastrService.error('Something went wrong!');
          this.changeDetectorRef.markForCheck();
          return of(null);
        }),
      )
      .subscribe((res: any) => {
        if (res.errors?.length) {
          this.isButtonDisabled = false;
          this.signUpForm.enable();
          this.signUpForm.reset();
        } else {
          this.router.navigate(['/']);
          this.isButtonDisabled = false;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  sendResetLink(): void {
    if (this.forgotPasswordForm.invalid) {
      return;
    }
    this.forgotPasswordForm.disable();
    this.showAlert = false;
    this.authService
      .forgotPassword(this.posId, this.forgotPasswordForm.get('email')?.value)
      .pipe(
        finalize(() => {
          this.forgotPasswordForm.enable();
          this.forgotPasswordForm.reset();
          this.showAlert = true;
        }),
        catchError(() => {
          this.toastrService.error('There is no user with this email!');
          this.changeDetectorRef.markForCheck();
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (response) {
          this.toastrService.success('Password reset sent! You will receive an email if you are registered on our system.');
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  forgotPwd() {
    this.activeId = 3;
    this.recoverPwd = true;
  }

  closeModal() {
    this.activeModal.dismissAll();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
  }
}
